import { Assets, Rectangle, Texture } from 'pixi.js';
import type { BakedLandManifest } from './bakedManifest';
import landManifestUrl from '../../assets/baked/land/land.json?url';
import landAtlasUrl from '../../assets/baked/land/land.webp?url';

/** Land tile textures keyed by tile name (`grass`, `dirt`, ...), as the map references them. */
export type LandTextures = Map<string, Texture>;

/**
 * Load the baked land-tile atlas and slice it into one texture per tile name.
 * The atlas + manifest come from the offline `art.mul`/UOP converter (each tile
 * a 44x44 dimetric diamond). Sampled `nearest` to keep the pixel art crisp.
 * Resolves once the atlas texture is ready so the renderer has no race.
 */
export async function loadLandTextures(): Promise<LandTextures> {
  // Fetched rather than inlined: the manifest tripled with the classic dungeon
  // bakes and the cold entry budget (ADR-0310) pays for every inlined byte.
  const [manifest, atlas] = await Promise.all([
    fetch(landManifestUrl).then((response) => {
      if (!response.ok) throw new Error(`land manifest ${response.status}: ${landManifestUrl}`);
      return response.json() as Promise<BakedLandManifest>;
    }),
    Assets.load<Texture>(landAtlasUrl),
  ]);
  atlas.source.scaleMode = 'nearest';

  const textures: LandTextures = new Map();
  for (const tile of manifest.tiles) {
    textures.set(
      tile.name,
      new Texture({ source: atlas.source, frame: new Rectangle(tile.x, tile.y, tile.w, tile.h) }),
    );
  }
  return textures;
}
